import type { ReactNode } from 'react';

export interface FormFieldProps {
  label: string;
  /** The child control's DOM id; the label's `htmlFor` and the hint/error ids derive from it. */
  htmlFor: string;
  required?: boolean | undefined;
  /** Labels on a royal/navy card. */
  dark?: boolean | undefined;
  /** Help text under the control. */
  hint?: string | undefined;
  /** Extra classes on the hint, e.g. hiding a nice-to-have hint where space is tight. */
  hintClassName?: string | undefined;
  /** Validation message under the control; replaces nothing, sits under the hint. */
  error?: string | undefined;
  children: ReactNode;
}

/** The id of a field's hint, for the control's aria-describedby. */
export function fieldHintId(htmlFor: string): string {
  return `${htmlFor}-hint`;
}

/** The id of a field's error message, for the control's aria-describedby. */
export function fieldErrorId(htmlFor: string): string {
  return `${htmlFor}-error`;
}

/**
 * The site's form field: label (a red asterisk when required, never
 * "(optional)"), the control, a hint below it, then any error.
 */
export function FormField({ label, htmlFor, required, dark = false, hint, hintClassName = '', error, children }: FormFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={htmlFor}
        className={`text-[13px] font-semibold ${dark ? 'text-white/85' : 'text-brand-navy/80'}`}
      >
        {label}
        {required ? <span className={dark ? 'text-red-300' : 'text-red-600'}> *</span> : null}
      </label>
      {children}
      {hint ? (
        <p id={fieldHintId(htmlFor)} className={`text-xs ${dark ? 'text-white/85' : 'text-brand-navy/70'} ${hintClassName}`}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={fieldErrorId(htmlFor)} role="alert" className={`text-xs font-semibold ${dark ? 'text-red-300' : 'text-red-600'}`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
